import React, { useMemo, useState } from 'react';
import { Card } from 'antd';
import { fetchSqlData } from '../../../services/api';
import { useQuery } from '@tanstack/react-query';
import type { AdmVehicle, Personnel, VwAdmVehicleFuelEntryEnriched } from '../../../types';
import StandardTable from '../../../components/common/StandardTable';
import FilterBar from '../../../components/common/FilterBar';
import { useTranslation } from 'react-i18next';
import { useGetPersonnel } from '../../../hooks/genericHooks';
import Button from '../../../components/common/Button';

const VehicleFuelHistory: React.FC = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === 'tr' ? 'tr-TR' : 'en-US';
  const [selectedVehicle, setSelectedVehicle] = useState<number | undefined>(undefined);
  const [selectedSourceType, setSelectedSourceType] = useState<string | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  const { data: vehicles = [] } = useQuery<AdmVehicle[], Error>({
    queryKey: ['machineryVehicles'],
    queryFn: () => fetchSqlData('adm_vehicles'),
    staleTime: 5 * 60_000,
  });
  const { data: personnelResp } = useGetPersonnel();
  const personnel = useMemo<Personnel[]>(() => {
    const arr = Array.isArray(personnelResp) ? personnelResp : (personnelResp as any)?.data || [];
    return arr as Personnel[];
  }, [personnelResp]);
  const { data: fuelEntries = [], isLoading, refetch } = useQuery<VwAdmVehicleFuelEntryEnriched[], Error>({
    queryKey: ['machineryFuelEntries'],
    queryFn: () => fetchSqlData('vw_adm_vehicle_fuel_entries_enriched'),
    staleTime: 60_000,
  });

  const vehiclesMap = useMemo(() => {
    const map: Record<number, string> = {};
    vehicles.forEach(vehicle => {
      if (vehicle.id) {
        map[vehicle.id] = `${vehicle.license_plate || '-'} ${vehicle.make || ''} ${vehicle.model || ''}`.trim();
      }
    });
    return map;
  }, [vehicles]);

  const personnelMap = useMemo(() => {
    const map: Record<number, string> = {};
    personnel.forEach(person => {
      if (person.id) {
        map[person.id] = `${person.first_name || ''} ${person.last_name || ''}`.trim();
      }
    });
    return map;
  }, [personnel]);

  const vehicleOptions = useMemo(
    () =>
      vehicles.map(vehicle => ({
        value: vehicle.id,
        label: vehiclesMap[vehicle.id] || String(vehicle.id),
      })),
    [vehicles, vehiclesMap]
  );

  const sourceTypeOptions = useMemo(
    () => [
      { value: 'fixed_pump', label: t('machinery.fuel.history.sourceTypeFixedPump') },
      { value: 'fuel_tanker', label: t('machinery.fuel.history.sourceTypeFuelTanker') },
      { value: 'external', label: t('machinery.fuel.history.sourceTypeExternal') },
    ],
    [t]
  );

  const filteredEntries = useMemo(() => {
    let rows = fuelEntries;

    if (selectedVehicle !== undefined) {
      rows = rows.filter(row => Number(row.vehicle_id) === Number(selectedVehicle));
    }

    if (selectedSourceType) {
      rows = rows.filter(row => row.source_type === selectedSourceType);
    }

    if (searchTerm.trim()) {
      const query = searchTerm.trim().toLowerCase();
      rows = rows.filter(row =>
        (vehiclesMap[row.vehicle_id] || '').toLowerCase().includes(query) ||
        (personnelMap[Number(row.personnel_id)] || '').toLowerCase().includes(query) ||
        (row.company || '').toLowerCase().includes(query) ||
        (row.fuel_station || '').toLowerCase().includes(query) ||
        (row.source_name || '').toLowerCase().includes(query) ||
        (row.source_pump_name || '').toLowerCase().includes(query) ||
        (row.source_vehicle_license_plate || '').toLowerCase().includes(query) ||
        (row.note || '').toLowerCase().includes(query)
      );
    }

    return rows;
  }, [fuelEntries, personnelMap, searchTerm, selectedSourceType, selectedVehicle, vehiclesMap]);

  const getSourceLabel = (row: VwAdmVehicleFuelEntryEnriched) => {
    if (row.source_type === 'fixed_pump') {
      return row.source_pump_name || row.source_name || row.fuel_station || '-';
    }

    if (row.source_type === 'fuel_tanker') {
      return `${row.source_vehicle_license_plate || '-'} ${row.source_vehicle_make || ''} ${row.source_vehicle_model || ''}`.trim();
    }

    return row.source_name || row.fuel_station || '-';
  };

  const getSourceTypeLabel = (value?: string | null) => {
    if (value === 'fixed_pump') return t('machinery.fuel.history.sourceTypeFixedPump');
    if (value === 'fuel_tanker') return t('machinery.fuel.history.sourceTypeFuelTanker');
    if (value === 'external') return t('machinery.fuel.history.sourceTypeExternal');
    return '-';
  };

  const columns = [
    {
      title: t('machinery.fuel.history.vehicle'),
      dataIndex: 'vehicle_id',
      key: 'vehicle_id',
      width: 180,
      render: (value: number) => vehiclesMap[value] || '-',
      exportValue: (row: VwAdmVehicleFuelEntryEnriched) => vehiclesMap[row.vehicle_id] || '-',
    },
    {
      title: t('machinery.fuel.history.date'),
      dataIndex: 'entry_date',
      key: 'entry_date',
      width: 120,
      render: (value: string) => (value ? new Date(value).toLocaleDateString(locale) : '-'),
    },
    {
      title: t('machinery.fuel.history.personnel'),
      dataIndex: 'personnel_id',
      key: 'personnel_id',
      width: 160,
      render: (value: number) => personnelMap[value] || '-',
      exportValue: (row: VwAdmVehicleFuelEntryEnriched) => personnelMap[Number(row.personnel_id)] || '-',
    },
    {
      title: t('machinery.fuel.history.fuelType'),
      dataIndex: 'fuel_type',
      key: 'fuel_type',
      width: 110,
      render: (value: string | null | undefined) => value || '-',
    },
    {
      title: t('machinery.fuel.history.sourceType'),
      dataIndex: 'source_type',
      key: 'source_type',
      width: 120,
      render: (value: string | null | undefined) => getSourceTypeLabel(value),
    },
    {
      title: t('machinery.fuel.history.source'),
      dataIndex: 'source_name',
      key: 'source_name',
      width: 180,
      render: (_: unknown, row: VwAdmVehicleFuelEntryEnriched) => getSourceLabel(row),
      exportValue: (row: VwAdmVehicleFuelEntryEnriched) => getSourceLabel(row),
    },
    {
      title: t('machinery.fuel.history.odometer'),
      dataIndex: 'odometer_reading',
      key: 'odometer_reading',
      width: 110,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale) || '-',
    },
    {
      title: t('machinery.fuel.history.hourCounter'),
      dataIndex: 'hours_counter_reading',
      key: 'hours_counter_reading',
      width: 110,
      align: 'right' as const,
      render: (value: number | null | undefined) => value?.toLocaleString(locale) || '-',
    },
    {
      title: t('machinery.fuel.history.liters'),
      dataIndex: 'liters',
      key: 'liters',
      width: 90,
      align: 'right' as const,
      render: (value: number) => value?.toLocaleString(locale, { maximumFractionDigits: 2 }) || '-',
    },
    { title: t('machinery.fuel.history.company'), dataIndex: 'company', key: 'company', width: 140 },
    { title: t('machinery.fuel.history.location'), dataIndex: 'location', key: 'location', width: 160 },
    { title: t('machinery.fuel.history.referenceNo'), dataIndex: 'reference_no', key: 'reference_no', width: 140 },
    { title: t('machinery.fuel.history.note'), dataIndex: 'note', key: 'note' },
  ];

  return (
    <Card title={t('machinery.fuel.history.title')} style={{ maxWidth: 1280, margin: '0 auto' }}>
      <FilterBar
        items={[
          { type: 'text', key: 'search', placeholder: t('machinery.fuel.history.searchPlaceholder'), value: searchTerm, onChange: value => setSearchTerm(value), width: 260 },
          { type: 'select', key: 'vehicle', placeholder: t('machinery.fuel.history.vehiclePlaceholder'), value: selectedVehicle as any, onChange: value => setSelectedVehicle(value), options: vehicleOptions, width: 260, allowClear: true, showSearch: true },
          { type: 'select', key: 'sourceType', placeholder: t('machinery.fuel.history.sourceTypePlaceholder'), value: selectedSourceType as any, onChange: value => setSelectedSourceType(value), options: sourceTypeOptions, width: 180, allowClear: true },
        ]}
        rightActions={<Button onClick={() => refetch()}>{t('machinery.fuel.history.refresh')}</Button>}
        onReset={() => {
          setSearchTerm('');
          setSelectedVehicle(undefined);
          setSelectedSourceType(undefined);
        }}
      />
      <StandardTable
        columns={columns}
        dataSource={filteredEntries}
        loading={isLoading}
        rowKey="id"
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: 'max-content' }}
        bordered
        size="small"
        enableCellTooltips
      />
    </Card>
  );
};

export default VehicleFuelHistory; 